import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, isSuper } from "@/lib/roles";

/** Permission matrix (view/create/edit/approve/publish/export/archive/delete per resource) and user access. */
export default function Permissions() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [role, setRole] = useState("admin");
  const [draft, setDraft] = useState({});
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    try {
      const [p, u] = await Promise.all([api.get("/rbac/permissions"), api.get("/users")]);
      setData(p.data); setUsers(u.data); setDraft(p.data.matrix[role] || {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [role]);
  useEffect(() => { load(); }, [load]);

  const toggle = (res, act) => {
    const cur = draft[res] || [];
    setDraft({ ...draft, [res]: cur.includes(act) ? cur.filter((a) => a !== act) : [...cur, act] });
  };
  const save = async () => {
    try { await api.put("/rbac/permissions", { role, permissions: draft }); toast.success(`Permissions saved for ${ROLE_LABELS[role]}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const reset = async () => {
    try { await api.post("/rbac/permissions/reset", { role }); toast.success("Reset to defaults"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setUserRole = async (u, r) => {
    const reason = window.prompt(`Reason for changing ${u.name || u.email} to ${ROLE_LABELS[r]}?`) ?? "";
    try { await api.patch(`/users/${u.id}/role`, { role: r, reason }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deactivate = async (u) => {
    if (!window.confirm(`${u.deactivated ? "Reactivate" : "Deactivate"} ${u.name || u.email}? History is kept.`)) return;
    try { await api.patch(`/users/${u.id}/deactivate`); toast.success("Saved"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!data) return <p className="text-muted-foreground">Loading…</p>;
  const editable = data.editable && role !== "super_admin";

  return (
    <div data-testid="permissions-page">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Security</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Roles & permissions</h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">Every permission is enforced by the server on each request — hiding a button is never the only protection. Super administrators keep full access and cannot erase audit history or view stored secrets.</p>

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-11 rounded-xl w-64 bg-white" data-testid="perm-role"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        {editable && <><Button onClick={save} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="perm-save-btn">Save matrix</Button>
          <Button variant="outline" onClick={reset} className="rounded-full font-bold" data-testid="perm-reset-btn">Reset to defaults</Button></>}
        {!editable && <p className="text-sm text-muted-foreground font-semibold">{role === "super_admin" ? "Super Administrator always has every permission." : "Only a super administrator can edit permissions."}</p>}
      </div>

      <div className="mt-5 bg-white rounded-2xl border border-border overflow-x-auto" data-testid="perm-matrix">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-xs uppercase tracking-wider"><th className="text-left p-3 font-bold">Resource</th>{data.actions.map((a) => <th key={a} className="p-3 font-bold text-center">{a}</th>)}</tr></thead>
          <tbody>
            {data.resources.map((res) => (
              <tr key={res} className="border-b border-border last:border-0" data-testid={`perm-row-${res}`}>
                <td className="p-3 font-bold capitalize">{res.replace("_", " ")}</td>
                {data.actions.map((a) => {
                  const on = role === "super_admin" || (draft[res] || []).includes(a);
                  return (
                    <td key={a} className="p-3 text-center">
                      <input type="checkbox" checked={on} disabled={!editable} onChange={() => toggle(res, a)} className="h-4 w-4" aria-label={`${role} ${a} ${res}`} data-testid={`perm-${res}-${a}`} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">User access</h2>
      <div className="mt-4 bg-white rounded-2xl border border-border overflow-x-auto" data-testid="user-access-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-xs uppercase tracking-wider">{["Name", "Email", "Role", "Sign-in", "Status", ""].map((h) => <th key={h} className="text-left p-3 font-bold">{h}</th>)}</tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={`border-b border-border last:border-0 ${u.deactivated ? "opacity-50" : ""}`} data-testid={`user-row-${u.id}`}>
                <td className="p-3 font-bold">{u.name}</td>
                <td className="p-3 text-muted-foreground">{u.email}</td>
                <td className="p-3">
                  {isSuper(user) && u.id !== user.id ? (
                    <Select value={u.role} onValueChange={(r) => setUserRole(u, r)}>
                      <SelectTrigger className="h-9 rounded-xl w-56" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-muted">{u.role_label}</span>}
                </td>
                <td className="p-3 text-xs text-muted-foreground">{(u.providers || []).join(", ") || "password"}</td>
                <td className="p-3"><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${u.deactivated ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>{u.deactivated ? "Deactivated" : "Active"}</span></td>
                <td className="p-3">{u.id !== user.id && <Button size="sm" variant="ghost" className="rounded-full text-xs font-bold" onClick={() => deactivate(u)} data-testid={`user-deactivate-${u.id}`}>{u.deactivated ? "Reactivate" : "Deactivate"}</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
